import React from "react";
import { css } from "@linaria/core";
import type { SingleValue } from "react-select";

import useToasts from "@ui/Toast/useToasts";
import t from "../../../../i18n";
import ReactSelect from "@ui/ReactSelect";
import Text from "@ui/Text";
import { usePost, usePut } from "../../../../api/useCall";
import call from "../../../../api/call";
import { useSources, type Source } from "../../../../api/useSources";
import Button from "@ui/Button";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, PlayCircleLine } from "@ui/IconV2/react";
import Modal from "@ui/Modal";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import formatDate from "@ui/i18n/formatDate";
import formatNumber from "@ui/i18n/formatNumber";
import { spacings } from "@ui/Theme";

interface Props {
  originalSettingsValue: string;
}

const parseSourceIds = (value: string) => [
  ...new Set(
    value
      .split(",")
      .map(id => id.trim())
      .filter(Boolean),
  ),
];

export const FreshDataForm = ({ originalSettingsValue }: Props) => {
  const [selectedIds, setSelectedIds] = React.useState(() => parseSourceIds(originalSettingsValue));
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    setSelectedIds(parseSourceIds(originalSettingsValue));
  }, [originalSettingsValue]);

  return (
    <div className={freshDataForm}>
      <Text type="body1" as="span">
        {t("customization.demo_environment.selected_sources", { count: selectedIds.length })}
      </Text>
      <Button type="button" variant="link" onClick={() => setIsOpen(true)}>
        {t("commons.details")}
      </Button>
      <Modal
        title={t("customization.demo_environment.keep_demos_updated")}
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        size="xl"
      >
        {isOpen && (
          <FreshDataModal selectedIds={selectedIds} onSelectedIdsChange={setSelectedIds} />
        )}
      </Modal>
    </div>
  );
};

interface ModalProps {
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
}

type SourceRow = { id: string; source: Source | undefined };
type Column = "name" | "sensorCount" | "snapshotCount" | "lastDate" | "action";

const FreshDataModal = ({ selectedIds, onSelectedIdsChange }: ModalProps) => {
  const sources = useSources();
  const updateSetting = usePut("/settings");
  const refreshSources = usePost("/sources/refresh-sources-dates");
  const { addToast } = useToasts();
  const [locked, setLocked] = React.useState<boolean | null>(null);
  const [saving, setSaving] = React.useState(false);
  const refreshing = React.useRef(false);
  const lockRequest = React.useRef(0);

  const readLock = React.useCallback(async () => {
    const request = ++lockRequest.current;
    try {
      const response = await call("GET", "/sources/refresh-sources-dates/status", {});
      if (request === lockRequest.current && !refreshing.current) {
        setLocked(response.body.running);
      }
    } catch {
      if (request === lockRequest.current && !refreshing.current) {
        setLocked(null);
      }
    }
  }, []);

  React.useEffect(() => {
    void readLock();
    const interval = window.setInterval(() => void readLock(), 2000);
    return () => window.clearInterval(interval);
  }, [readLock]);

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  const rows = React.useMemo<SourceRow[]>(
    () => selectedIds.map(id => ({ id, source: sources.find(source => source.id === id) })),
    [selectedIds, sources],
  );
  const sourceOptions = React.useMemo(
    () =>
      sources
        .filter(source => !selectedSet.has(source.id))
        .map(source => ({ label: source.id, value: source.id })),
    [selectedSet, sources],
  );
  const actionsDisabled = locked !== false || saving || refreshSources.loading;

  const saveSelection = React.useCallback(
    async (ids: string[]) => {
      if (actionsDisabled) return;
      setSaving(true);
      try {
        await updateSetting({ body: { FRESHLY_MAINTAINED_SOURCES: ids.join(",") } });
        onSelectedIdsChange(ids);
        addToast("success", t("customization.demo_environment.keep_demos_updated_form.toast"));
      } catch {
        await readLock();
      } finally {
        setSaving(false);
      }
    },
    [actionsDisabled, updateSetting, onSelectedIdsChange, addToast, readLock],
  );

  const refresh = React.useCallback(
    async (sourceId?: string) => {
      if (actionsDisabled || refreshing.current) return;
      refreshing.current = true;
      setLocked(true);
      try {
        await refreshSources({ query: sourceId ? { sourceId } : {} });
        addToast("success", t("customization.demo_environment.refresh_sources.toast"));
      } catch {
        // usePost displays the request error.
      } finally {
        refreshing.current = false;
        await readLock();
      }
    },
    [actionsDisabled, refreshSources, addToast, readLock],
  );

  const metadata = React.useMemo<{ [key in Column]: ColumnMetaData<SourceRow> }>(
    () => ({
      name: {
        label: t("sources.table.column.name"),
        render: row => row.id,
        minWidth: "200px",
      },
      sensorCount: {
        label: t("sources.table.column.sensorCount"),
        render: row => (row.source ? formatNumber(row.source._count.sensors) : null),
        width: "120px",
      },
      snapshotCount: {
        label: t("sources.table.column.snapshotCount"),
        render: row => (row.source ? formatNumber(row.source.snapshotCount) : null),
        width: "140px",
      },
      lastDate: {
        label: t("sources.table.column.lastDate"),
        render: row =>
          row.source?.maxSnapshotDate
            ? formatDate(row.source.maxSnapshotDate, "datetime", null)
            : null,
        width: "180px",
      },
      action: {
        label: t("customization.demo_environment.action"),
        render: row => (
          <div className={sourceActions}>
            <ButtonIconV2
              title={t("customization.demo_environment.refresh_source")}
              disabled={actionsDisabled || !row.source}
              onClick={() => void refresh(row.id)}
            >
              <PlayCircleLine size="m" />
            </ButtonIconV2>
            <ButtonIconV2
              title={t("customization.demo_environment.remove_source")}
              disabled={actionsDisabled}
              onClick={() => void saveSelection(selectedIds.filter(id => id !== row.id))}
            >
              <DeleteBinLine size="m" />
            </ButtonIconV2>
          </div>
        ),
        width: "110px",
      },
    }),
    [actionsDisabled, refresh, saveSelection, selectedIds],
  );

  return (
    <div className={freshDataModal}>
      <div className={sourceSearch}>
        <ReactSelect
          menuPortalTarget={window.document.body}
          value={null}
          options={sourceOptions}
          onChange={(option: SingleValue<(typeof sourceOptions)[number]>) => {
            if (option !== null) void saveSelection([...selectedIds, option.value]);
          }}
          placeholder={t("customization.demo_environment.search_sources")}
          isDisabled={actionsDisabled}
          isClearable={false}
        />
      </div>
      <Text type="body2" className={lockMessage} role="status" aria-live="polite">
        {locked === true && t("customization.demo_environment.refresh_in_progress")}
        {locked === null && t("customization.demo_environment.checking_refresh_status")}
      </Text>
      <Table datas={rows} metadata={metadata} computeRowKey={row => row.id} />
      <div className={refreshActions}>
        <Button
          type="button"
          variant="primary"
          disabled={actionsDisabled || selectedIds.length === 0}
          onClick={() => void refresh()}
        >
          {t("customization.demo_environment.refresh_sources.button")}
        </Button>
      </div>
    </div>
  );
};

const freshDataForm = css`
  display: flex;
  align-items: center;
  gap: ${spacings.m};
`;

const freshDataModal = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.m};
  min-width: 0;
`;

const sourceSearch = css`
  width: 100%;
`;

const lockMessage = css`
  min-height: 36px;
  width: 100%;
`;

const refreshActions = css`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const sourceActions = css`
  display: flex;
  align-items: center;
`;
